import React, { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import {
  ShieldCheck, ShieldOff, ShieldAlert, Loader2, Copy, ExternalLink, Search,
  Award,
} from "lucide-react";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CertificateVerify() {
  const { cert_number } = useParams();
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, data: null });
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!cert_number) { setState({ loading: false, data: null }); return; }
    let alive = true;
    (async () => {
      setState({ loading: true, data: null });
      try {
        const r = await axios.get(`${API}/public/certificates/verify/${encodeURIComponent(cert_number)}`);
        if (alive) setState({ loading: false, data: r.data });
      } catch (e) {
        if (alive) setState({ loading: false, data: { verification_status: "not_found", cert_number } });
      }
    })();
    return () => { alive = false; };
  }, [cert_number]);

  const submit = (e) => {
    e.preventDefault();
    const clean = search.trim().toUpperCase();
    if (!clean) return;
    navigate(`/certificates/verify/${encodeURIComponent(clean)}`);
  };
  const copy = async () => {
    await navigator.clipboard.writeText(window.location.href);
    toast.success("Verification link copied.");
  };

  return (
    <div className="min-h-screen bg-[var(--cream)]" data-testid="verify-page">
      <SEO title={cert_number ? `Verify ${cert_number} · CollectorUp` : "Verify a certificate · CollectorUp"} />

      <div className="container-gm py-10 md:py-14">
        <div className="text-center max-w-2xl mx-auto">
          <div className="inline-flex items-center gap-1.5 text-xs uppercase tracking-widest font-bold text-[var(--panini-red)]">
            <Award size={13} /> CollectorUp Certificate Verification
          </div>
          <h1 className="font-display text-4xl md:text-5xl mt-3">Verify a certificate.</h1>
          <p className="text-sm text-[var(--smoke)] mt-3">
            Every CollectorUp certificate is issued by Gil Maakaroun and can be verified permanently here.
            Enter a certificate number below to confirm its authenticity.
          </p>
        </div>

        <form onSubmit={submit} className="mt-8 max-w-xl mx-auto flex items-stretch gap-2" data-testid="verify-search-form">
          <div className="flex-1 relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="verify-search-input"
              placeholder="e.g. GM-2026-0001"
              className="w-full border-2 border-[var(--ink)] rounded-lg pl-9 pr-3 py-2.5 text-sm bg-white font-mono uppercase"
            />
          </div>
          <button type="submit" className="btn-red text-sm" data-testid="verify-search-submit">
            Verify
          </button>
        </form>

        {cert_number && (
          <div className="mt-10 max-w-3xl mx-auto">
            {state.loading ? (
              <div className="rounded-2xl border-2 border-[var(--ink)] bg-white p-16 grid place-items-center text-[var(--smoke)]" data-testid="verify-loading">
                <Loader2 className="animate-spin" size={30} />
              </div>
            ) : (
              <ResultCard data={state.data} onCopy={copy} />
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function ResultCard({ data, onCopy }) {
  if (!data) return null;
  if (data.verification_status === "valid") return <ValidCard data={data} onCopy={onCopy} />;
  if (data.verification_status === "revoked") return <RevokedCard data={data} onCopy={onCopy} />;
  return <NotFoundCard cert={data.cert_number} />;
}

function ValidCard({ data, onCopy }) {
  return (
    <div
      className="rounded-2xl border-4 border-emerald-900 bg-white overflow-hidden shadow-[8px_8px_0_var(--ink)]"
      data-testid="verify-valid"
    >
      <div className="bg-emerald-600 text-white px-6 py-4 flex items-center gap-3">
        <ShieldCheck size={28} />
        <div>
          <div className="font-display text-2xl leading-none">Certificate Verified</div>
          <div className="text-[11px] uppercase tracking-widest font-bold mt-1 opacity-90">Verified by CollectorUp</div>
        </div>
      </div>
      <div className="p-6 md:p-8">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Certificate number</div>
        <div className="font-mono font-bold text-2xl md:text-3xl break-all mt-1" data-testid="verify-valid-number">{data.cert_number}</div>

        <div className="mt-6 grid md:grid-cols-2 gap-4 text-sm">
          {data.collector?.display_name && (
            <Field label="Collector">
              <div className="font-bold" data-testid="verify-valid-collector-name">{data.collector.display_name}</div>
              {data.collector.username && (
                <Link
                  to={data.collector.profile_url || `/collectors/${data.collector.username}`}
                  className="text-xs text-[var(--panini-red)] font-bold hover:underline inline-flex items-center gap-1 mt-1"
                  data-testid="verify-valid-collector-link"
                >
                  @{data.collector.username} <ExternalLink size={11} />
                </Link>
              )}
            </Field>
          )}
          {data.album_title && (
            <Field label="Album">
              <div className="font-bold" data-testid="verify-valid-album">{data.album_title}</div>
              {data.album_edition && (
                <div className="text-xs text-[var(--smoke)] mt-0.5">{data.album_edition}</div>
              )}
            </Field>
          )}
          {data.completion_date && (
            <Field label="Completion date"><div className="font-bold" data-testid="verify-valid-completion">{data.completion_date}</div></Field>
          )}
          {data.issued_at && (
            <Field label="Issue date"><div className="font-bold" data-testid="verify-valid-issued">{new Date(data.issued_at).toLocaleDateString()}</div></Field>
          )}
        </div>

        {data.public_notes && (
          <div className="mt-6 p-4 rounded-lg border-2 border-[var(--panini-yellow)] bg-[var(--panini-yellow-soft)] text-sm" data-testid="verify-valid-notes">
            {data.public_notes}
          </div>
        )}

        <div className="mt-6 pt-5 border-t-2 border-[var(--stone)] flex items-center justify-between flex-wrap gap-3 text-xs">
          <div className="text-[var(--smoke)]">
            This certificate is authentic and recognised in the CollectorUp registry.
          </div>
          <button type="button" onClick={onCopy} className="btn-ghost text-xs" data-testid="verify-valid-copy">
            <Copy size={11} /> Copy verification link
          </button>
        </div>
      </div>
    </div>
  );
}

function RevokedCard({ data, onCopy }) {
  return (
    <div className="rounded-2xl border-4 border-red-900 bg-white overflow-hidden shadow-[8px_8px_0_var(--ink)]" data-testid="verify-revoked">
      <div className="bg-red-700 text-white px-6 py-4 flex items-center gap-3">
        <ShieldOff size={28} />
        <div>
          <div className="font-display text-2xl leading-none">Certificate Revoked</div>
          <div className="text-[11px] uppercase tracking-widest font-bold mt-1 opacity-90">This certificate is no longer valid</div>
        </div>
      </div>
      <div className="p-6 md:p-8">
        <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">Certificate number</div>
        <div className="font-mono font-bold text-2xl break-all mt-1" data-testid="verify-revoked-number">{data.cert_number}</div>
        {data.revoked_at && (
          <div className="mt-4 text-sm text-[var(--smoke)]" data-testid="verify-revoked-date">
            Revoked on <span className="font-bold text-[var(--ink)]">{new Date(data.revoked_at).toLocaleDateString()}</span>.
          </div>
        )}
        {data.public_notes && (
          <div className="mt-4 p-4 rounded-lg border-2 border-red-900 bg-red-50 text-sm" data-testid="verify-revoked-notes">
            {data.public_notes}
          </div>
        )}
        <div className="mt-6 pt-5 border-t-2 border-[var(--stone)] flex items-center justify-between text-xs">
          <div className="text-[var(--smoke)]">If you believe this is an error, please contact CollectorUp.</div>
          <button type="button" onClick={onCopy} className="btn-ghost text-xs" data-testid="verify-revoked-copy">
            <Copy size={11} /> Copy link
          </button>
        </div>
      </div>
    </div>
  );
}

function NotFoundCard({ cert }) {
  return (
    <div className="rounded-2xl border-4 border-[var(--ink)] bg-white overflow-hidden shadow-[8px_8px_0_var(--ink)]" data-testid="verify-not-found">
      <div className="bg-[var(--ink)] text-white px-6 py-4 flex items-center gap-3">
        <ShieldAlert size={28} />
        <div>
          <div className="font-display text-2xl leading-none">Certificate Not Found</div>
          <div className="text-[11px] uppercase tracking-widest font-bold mt-1 opacity-90">Nothing matched this certificate number</div>
        </div>
      </div>
      <div className="p-6 md:p-8 text-sm text-[var(--smoke)]">
        <p>
          No CollectorUp certificate matches <span className="font-mono font-bold text-[var(--ink)]">{cert}</span>.
          Double-check the number and try again — certificate numbers are exact.
        </p>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--smoke)]">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
